/**
 * D217: a phrase read as a PLURAL, in the clause's terms — one thing, a set,
 * or the which-one question a set cannot answer in a bound phrase.
 *
 * @packageDocumentation
 */

import { type PhraseToken } from '../grammar/match-clause-structure.js';
import { type ClarificationRequest, type ParseCandidateEntity } from '../types.js';
import { type ResolvedEntityPhrase, resolvePluralPhrase } from './resolve-entity-phrase.js';

/** What a plural reading gives the clause: a resolution (one thing, or a set), or a question. */
export type PluralPhraseReading =
	| { readonly clarification: ClarificationRequest; readonly type: 'clarification' }
	| { readonly resolution: ResolvedEntityPhrase; readonly type: 'resolved' };

/**
 * Read a phrase that placed nobody as written as a plural (see
 * `resolvePluralPhrase`).
 *
 * @remarks
 * One member is simply that thing. Several are a SET: in the direct object
 * the set rides on the resolution for the consumer's policy to answer; in a
 * bound phrase (`put the coin in the open jars`) a set cannot be a tool or a
 * destination at once, so it is the which-one question it always was.
 *
 * @param phrase - The phrase tokens
 * @param entities - What is currently referable
 * @param isBoundPhrase - Whether the phrase is bound by a preposition rather than the direct object
 * @param clauseText - The clause, folded (a question cites it)
 * @returns The reading, or `undefined` when the plural reading places nobody
 */
export const readPhraseAsPlural = (
	phrase: readonly PhraseToken[],
	entities: readonly ParseCandidateEntity[],
	isBoundPhrase: boolean,
	clauseText: string,
): PluralPhraseReading | undefined => {
	const pluralReading = resolvePluralPhrase(phrase, entities);

	if (pluralReading === undefined) {
		return undefined;
	}

	const [firstMember, ...otherMembers] = pluralReading.entityRefs;
	const isSet = otherMembers.length > 0;

	if (isSet && isBoundPhrase) {
		return {
			clarification: {
				candidates: pluralReading.entityRefs.map((entityRef) => ({
					entityRef,
					matchedTokens: phrase.map((phraseToken) => phraseToken.sourceText),
				})),
				clauseText,
				kind: 'which_entity',
			},
			type: 'clarification',
		};
	}

	return {
		resolution: {
			entityRef: firstMember,
			// D226 (E20): the plural word as typed is the head the player used.
			headSurface: phrase.slice(-1).map((phraseToken) => phraseToken.sourceText).join(''),
			qualifiers: pluralReading.qualifiers,
			wasAliasMatched: false,
			...(isSet ? { setEntityRefs: pluralReading.entityRefs } : {}),
		},
		type: 'resolved',
	};
};
